/**
 * PipelineView — the single pane's content body (Wave 2c integration).
 *
 * Composes the locked layout set into one recency-sorted view of the
 * trial-execution pipeline on this host: a PipelineBar (segmented lane counts +
 * throughput), a FidelityRocker (trial ↔ run zoom), a FilterBar (cross-run status
 * chips + search), and — per fidelity — either the interleaved ActivityStream
 * (trial rows with the per-run color dot) or the RunSparklineRow rollup.
 *
 * Data ownership: the App owns the two polls (`/runs`, `/host-lens`) and passes
 * snapshots down. This component owns only local UI state — the fidelity zoom, the
 * trial-board layout (list ↔ Kanban, persisted per session), and the filter —
 * none of which touches the network. Switching fidelity, flipping the layout, or
 * typing a query re-renders over the already-loaded snapshots, never refetches.
 *
 * At trial fidelity the board renders either the dense recency-sorted
 * ActivityStream (list) or the same trials grouped into the five status lanes
 * (Kanban); the run-fidelity rollup is a one-line-per-run list, so the layout
 * toggle is only meaningful — and only shown — at trial fidelity.
 *
 * Off-happy-path: each fidelity surfaces its own loading / error / empty /
 * partial states honestly — a first-poll failure shows the error (nothing else is
 * true yet); a mid-session failure keeps the last snapshot and ages a banner
 * rather than blanking.
 */

import { useCallback, useState } from 'react'
import { ActivityStream } from './ActivityStream'
import { type Fidelity, FidelityRocker } from './FidelityRocker'
import { FilterBar } from './FilterBar'
import { KanbanBoard } from './KanbanBoard'
import { LayoutToggle } from './LayoutToggle'
import { PipelineBar } from './PipelineBar'
import { RunKanbanBoard } from './RunKanbanBoard'
import { RunSparklineRow } from './RunSparklineRow'
import type { CancelRunResult } from './board-client'
import { type BoardFilter, NO_FILTER, filterTrials } from './board-concurrency'
import { laneCounts, laneForStatus } from './board-lanes'
import { type BoardLayout, readLayout, writeLayout } from './board-layout'
import type { HostLensSnapshot } from './board-types'
import {
  EMPTY_STATUS_COUNTS,
  filterRuns,
  hasOutstandingWork,
  pipelineThroughput,
  sortTrialsByRecency,
} from './pipeline-data'
import type { RunsSnapshot } from './types'

/** A surfaced read error (endpoint + message), or null. */
type ReadError = { endpoint: string; message: string } | null

/** Scope: the whole host, or only the terminal tail (the History view). */
export type PipelineScope = 'all' | 'finished'

export interface PipelineViewProps {
  /** Page title rendered at the left of the toolbar (Pipeline / Runs / History). */
  title: string
  /** Latest `/runs` rollup (recency-sorted + finished-capped server-side), or null while loading. */
  runs: RunsSnapshot | null
  /** Latest `/host-lens` interleave, or null while loading. */
  lens: HostLensSnapshot | null
  /** A retained `/runs` error to surface, or null. */
  runsError: ReadError
  /** A retained `/host-lens` error to surface, or null. */
  lensError: ReadError
  /** Landing fidelity for this route — the rocker takes over locally after mount. */
  initialFidelity: Fidelity
  /** 'finished' restricts the view to terminal runs / done trials (History). */
  scope: PipelineScope
  /** Opens the trial drawer — the App writes `#trial=<id>` from this. */
  onSelectTrial: (trialId: string) => void
  /**
   * Fires a run cancel (`POST /runs/<id>/cancel`). Threaded to the run-fidelity
   * rows, which show a Cancel control on live runs only. Omitted in views/tests
   * that don't wire cancellation.
   */
  onCancelRun?: (runId: string) => Promise<CancelRunResult>
  /** Called once a cancel succeeds, so the App can re-fetch the run rollup. */
  onRunCancelled?: () => void
}

export function PipelineView({
  title,
  runs,
  lens,
  runsError,
  lensError,
  initialFidelity,
  scope,
  onSelectTrial,
  onCancelRun,
  onRunCancelled,
}: PipelineViewProps): JSX.Element {
  const [fidelity, setFidelity] = useState<Fidelity>(initialFidelity)
  const [filter, setFilter] = useState<BoardFilter>(NO_FILTER)
  // The list ↔ Kanban choice persists per session (board-layout.ts), so it
  // survives a nav between views and a soft reload without leaking into deep links.
  const [layout, setLayout] = useState<BoardLayout>(readLayout)
  const onLayoutChange = useCallback((next: BoardLayout) => {
    setLayout(next)
    writeLayout(next)
  }, [])

  const lanes = laneCounts(lens?.counts ?? EMPTY_STATUS_COUNTS, lens?.recovering ?? 0)
  const throughput = Math.round(pipelineThroughput(runs?.runs ?? []))

  return (
    <div className="pipeline-view" data-testid="pipeline-view">
      <div className="pipeline-view-toolbar">
        <h1 className="pipeline-view-title">{title}</h1>
        <div className="pipeline-view-controls">
          {/* Layout toggle: governs the trial board (list/kanban) and the run view (list/kanban). */}
          <LayoutToggle value={layout} onChange={onLayoutChange} />
          <FidelityRocker value={fidelity} onChange={setFidelity} />
        </div>
      </div>

      <PipelineBar laneCounts={lanes} throughputPerMin={throughput} />
      <FilterBar
        filter={filter}
        onChange={setFilter}
        hideLaneChips={fidelity === 'run'}
        placeholder={fidelity === 'run' ? 'filter run / name' : undefined}
        ariaLabel={fidelity === 'run' ? 'filter runs' : undefined}
      />

      <div className="pipeline-view-body">
        {fidelity === 'trial'
          ? renderTrialFidelity(lens, lensError, scope, filter, layout, onSelectTrial)
          : renderRunFidelity(runs, runsError, scope, filter, layout, onCancelRun, onRunCancelled)}
      </div>
    </div>
  )
}

/** The interleaved trial stream (trial fidelity) — list or Kanban per the layout. */
function renderTrialFidelity(
  lens: HostLensSnapshot | null,
  lensError: ReadError,
  scope: PipelineScope,
  filter: BoardFilter,
  layout: BoardLayout,
  onSelectTrial: (trialId: string) => void
): JSX.Element {
  if (lens === null) {
    return lensError ? (
      <ErrorState endpoint={lensError.endpoint} message={lensError.message} />
    ) : (
      <LoadingState label="loading pipeline…" />
    )
  }
  const scoped = scope === 'finished' ? lens.trials.filter((t) => laneForStatus(t.status) === 'done') : lens.trials
  const visible = filterTrials(sortTrialsByRecency(scoped), filter)
  return (
    <>
      {lensError ? <StaleBanner endpoint={lensError.endpoint} /> : null}
      {layout === 'kanban' ? (
        <KanbanBoard
          trials={visible}
          onSelect={onSelectTrial}
          // Only pass authoritative counts when the view is unfiltered and unscoped.
          // Scope='finished' and active filters intentionally narrow `visible`, so a
          // simple loaded-vs-total comparison would produce misleading "0 of N" labels
          // on non-Done lanes or "3 of 500" reads as a pagination ceiling rather than
          // a filter result.
          authoritativeCounts={scope === 'all' && filter === NO_FILTER ? lens.counts : undefined}
        />
      ) : (
        <ActivityStream trials={visible} onSelect={onSelectTrial} />
      )}
    </>
  )
}

/** The run rollup view (run fidelity) — list or kanban per the layout. */
function renderRunFidelity(
  runs: RunsSnapshot | null,
  runsError: ReadError,
  scope: PipelineScope,
  filter: BoardFilter,
  layout: BoardLayout,
  onCancelRun?: (runId: string) => Promise<CancelRunResult>,
  onRunCancelled?: () => void
): JSX.Element {
  if (runs === null) {
    return runsError ? (
      <ErrorState endpoint={runsError.endpoint} message={runsError.message} />
    ) : (
      <LoadingState label="loading runs…" />
    )
  }
  // The server already recency-sorts and caps the finished tail; we only narrow
  // to the terminal runs for the History scope.
  const scoped = scope === 'finished' ? runs.runs.filter((r) => !hasOutstandingWork(r.counts)) : runs.runs
  // Apply free-text filter (lane chips are hidden at run fidelity).
  const visible = filterRuns(scoped, filter)
  return (
    <>
      {runsError ? <StaleBanner endpoint={runsError.endpoint} /> : null}
      {visible.length === 0 ? (
        scoped.length > 0 ? (
          // Runs exist but the current query matches none of them.
          <div className="state state-empty" data-testid="empty-state">
            <p className="state-title">no runs match your filter</p>
            <p className="state-detail">clear the search to see all runs.</p>
          </div>
        ) : (
          // No runs exist at all for this scope.
          <div className="state state-empty" data-testid="empty-state">
            <p className="state-title">{scope === 'finished' ? 'no finished runs yet' : 'no runs on this host yet'}</p>
            <p className="state-detail">trigger a run and it will appear here.</p>
          </div>
        )
      ) : layout === 'kanban' ? (
        <RunKanbanBoard runs={visible} onCancelRun={onCancelRun} onRunCancelled={onRunCancelled} />
      ) : (
        <ul className="pipeline-run-list" data-testid="run-list">
          {visible.map((run) => (
            <RunSparklineRow
              key={run.runId}
              run={run}
              throughputSeries={[]}
              onCancelRun={onCancelRun}
              onRunCancelled={onRunCancelled}
            />
          ))}
        </ul>
      )}
    </>
  )
}

function LoadingState({ label }: { label: string }): JSX.Element {
  return (
    <div className="state state-loading" data-testid="loading-state">
      <p className="state-title">{label}</p>
    </div>
  )
}

function ErrorState({ endpoint, message }: { endpoint: string; message: string }): JSX.Element {
  return (
    <div className="state state-error" role="alert" data-testid="error-state">
      <p className="state-title">could not reach {endpoint}</p>
      <p className="state-detail">{message}</p>
    </div>
  )
}

function StaleBanner({ endpoint }: { endpoint: string }): JSX.Element {
  return (
    <div className="state-banner" role="alert" data-testid="error-banner">
      lost contact with {endpoint} — showing the last snapshot
    </div>
  )
}
